/**
 * Slim enrichment-review PATCH payloads and single-flight coalescing.
 * Heavy preview fields (e.g. tool_results) stay on the server; only review deltas are sent.
 */

const MERGE_MODE_KEY = '_review_persist_mode';
const MERGE_MODE_VALUE = 'merge';

/**
 * @param {Record<string, unknown>|null|undefined} preview
 * @returns {Record<string, unknown>|null}
 */
export function buildEnrichmentReviewPersistPayload(preview) {
  if (!preview || typeof preview !== 'object') return null;

  const outcomes = preview.enrichment_review_outcomes;
  const slim = {
    [MERGE_MODE_KEY]: MERGE_MODE_VALUE,
    enrichment_review_outcomes:
      outcomes && typeof outcomes === 'object' ? outcomes : {},
    proposed_relationships: preview.proposed_relationships ?? [],
    proposed_catalog_entities: preview.proposed_catalog_entities ?? [],
    resolution: preview.resolution ?? [],
  };

  if (preview.description_proposal != null) {
    slim.description_proposal = preview.description_proposal;
  }
  if (preview.display_name_overrides != null) {
    slim.display_name_overrides = preview.display_name_overrides;
  }

  return slim;
}

/**
 * Stable hash for skipping redundant PATCHes (e.g. duplicate close flushes).
 * @param {Record<string, unknown>|null|undefined} preview
 */
export function enrichmentReviewPersistFingerprint(preview) {
  const body = buildEnrichmentReviewPersistPayload(preview);
  if (!body) return '';
  try {
    return JSON.stringify(body);
  } catch {
    return String(Date.now());
  }
}

const inFlightByJob = new Map();
const pendingByJob = new Map();
const lastFingerprintByJob = new Map();

/**
 * @param {string} jobId
 * @param {Record<string, unknown>|null|undefined} preview
 * @param {{ auditEvent?: Record<string, unknown>, force?: boolean }} [opts]
 * @param {(jobId: string, body: { preview: object, audit_event?: object }) => Promise<unknown>} patchFn
 */
export async function persistEnrichmentReviewCoalesced(
  jobId,
  preview,
  opts,
  patchFn
) {
  if (!jobId || !preview) return;
  const auditEvent = opts?.auditEvent;
  const slim = buildEnrichmentReviewPersistPayload(preview);
  if (!slim) return;

  const fingerprint = enrichmentReviewPersistFingerprint(preview);
  if (!opts?.force && fingerprint && fingerprint === lastFingerprintByJob.get(jobId)) {
    return;
  }

  pendingByJob.set(jobId, { slim, auditEvent, fingerprint });

  if (inFlightByJob.get(jobId)) {
    return inFlightByJob.get(jobId);
  }

  const run = (async () => {
    while (pendingByJob.has(jobId)) {
      const snap = pendingByJob.get(jobId);
      pendingByJob.delete(jobId);
      if (!snap) break;
      const body = { preview: snap.slim };
      if (snap.auditEvent) body.audit_event = snap.auditEvent;
      await patchFn(jobId, body);
      if (snap.fingerprint) lastFingerprintByJob.set(jobId, snap.fingerprint);
    }
  })();

  inFlightByJob.set(jobId, run);
  try {
    await run;
  } finally {
    inFlightByJob.delete(jobId);
  }
}

export function resetEnrichmentReviewPersistCoalesce(jobId) {
  if (!jobId) return;
  pendingByJob.delete(jobId);
  inFlightByJob.delete(jobId);
  lastFingerprintByJob.delete(jobId);
}
